/*
 * Shared reading of `JobProgress`, used by two surfaces that describe the same
 * job at different resolutions:
 *
 *   - `JobProgressChecklist` — the full four-row checklist on the submission and
 *     content pages, one row per stage with its own counts.
 *   - the sidebar's in-progress card — one line: which stage is running, how far
 *     along overall, and how long is left.
 *
 * They were written independently and drifted, which is the reason this file
 * exists rather than each keeping its own copy.
 */
import type { JobProgress, ProcessingJob } from '@/shared/types';

/**
 * A stage is done once its ready count catches its total — and only if there was
 * anything to do, since `0/0` means "no plan yet", not "finished".
 *
 * `>=` rather than `===`, which is the one place the two former copies actually
 * disagreed. The backend recomputes totals as the story plan settles, so a total
 * that shrinks after some items are ready would, under `===`, flip a completed
 * stage back to pending and walk the progress bar backwards.
 */
export function stageDone(ready: number, total: number): boolean {
  return total > 0 && ready >= total;
}

export type ProgressStage = {
  label: string;
  done: boolean;
  /** 0–1 within this stage. */
  fraction: number;
};

/** The render pipeline in the order the backend runs it. */
export function progressStages(progress: JobProgress): ProgressStage[] {
  const ratio = (ready: number, total: number) => (total > 0 ? Math.min(1, ready / total) : 0);

  return [
    {
      label: 'Designing characters',
      done: stageDone(progress.characters.ready, progress.characters.total),
      fraction: ratio(progress.characters.ready, progress.characters.total),
    },
    {
      label: 'Composing keyframes',
      done: stageDone(progress.keyframes.ready, progress.keyframes.total),
      fraction: ratio(progress.keyframes.ready, progress.keyframes.total),
    },
    {
      label: 'Generating narration',
      done: progress.narrationReady,
      fraction: progress.narrationReady ? 1 : 0,
    },
    {
      label: 'Rendering scenes',
      done: stageDone(progress.scenes.completed, progress.scenes.total),
      fraction: ratio(progress.scenes.completed, progress.scenes.total),
    },
  ];
}

export type ProgressSummary = {
  /** The stage currently running, or `Finalizing` once they are all done. */
  label: string;
  /** 0–1 across the whole pipeline. */
  fraction: number;
  /** True when there is no meaningful number to show yet. */
  indeterminate: boolean;
};

/**
 * Collapses a job to the single line the sidebar card shows.
 *
 * Deliberately takes the whole job, not just `progress`: a queued job has no
 * progress at all, and "Queued" is the honest label for it — `0%` would suggest
 * work is underway.
 */
export function summarizeProgress(job: ProcessingJob): ProgressSummary {
  if (job.status === 'queued') return { label: 'Queued', fraction: 0, indeterminate: true };
  /* Nothing is running: the engine has planned the story, put a hold on the
     user's credits and stopped. This reported `fraction: 1` and rendered beside
     a spinner, so the one always-visible surface described a job blocked on the
     user as one about to finish — and the longer nobody answered, the more
     convincing it looked. Indeterminate is the honest reading, and the label
     says whose move it is. */
  if (job.status === 'awaiting_confirmation') {
    return { label: 'Awaiting your confirmation', fraction: 0, indeterminate: true };
  }

  const progress = job.progress;
  if (!progress) return { label: 'Preparing', fraction: 0, indeterminate: true };

  const stages = progressStages(progress);
  const running = stages.find((stage) => !stage.done);
  const fraction = stages.reduce((sum, stage) => sum + stage.fraction, 0) / stages.length;

  return {
    label: running ? running.label : 'Finalizing',
    fraction: Math.min(1, Math.max(0, fraction)),
    indeterminate: false,
  };
}

/**
 * Coarse enough — seconds below a minute, whole minutes above — that a 1s local
 * tick doesn't visibly flicker the displayed unit.
 *
 * One phrasing, deliberately. The checklist and the sidebar card can be on
 * screen together — the rail is always visible — and describing the same job as
 * `~3 min remaining` in one place and `About 3 min left` in the other reads as a
 * bug. This is the checklist's shipped wording, which is also the shorter of the
 * two and so the better fit for a 264px card.
 */
export function formatEta(seconds: number): string {
  // NaN fails every comparison below, so without this it fell through to
  // "~NaN min remaining" on the rail card. An unparseable ETA and an expired
  // one deserve the same modest wording — never a number we don't have.
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Almost done';
  if (seconds < 60) return `~${seconds} sec remaining`;
  return `~${Math.round(seconds / 60)} min remaining`;
}

/**
 * The live estimate, preferring the structured one. `ProcessingJob.etaSeconds`
 * is the coarse value the submit response carries; `progress.etaSeconds` is the
 * backend's self-calibrating estimate and supersedes it once it arrives.
 */
export function etaSecondsFor(job: ProcessingJob): number | undefined {
  return job.progress?.etaSeconds ?? job.etaSeconds;
}

const ACTIVE_STATUSES: ProcessingJob['status'][] = ['queued', 'processing', 'awaiting_confirmation'];

/** Jobs still doing something — what the sidebar's in-progress list is made of. */
export function isActiveJob(job: ProcessingJob): boolean {
  return ACTIVE_STATUSES.includes(job.status);
}
